import { getAccessToken, isImpersonationTab } from '@/backend/auth/tokenStorage.mjs'
import {
  deletePushSubscription,
  fetchVapidPublicKey,
  savePushSubscription,
} from '@/backend/messageServiceApi'
import {
  isWebPushSupported,
  parseVapidKeyPayload,
  readStoredVapidKey,
  shouldReplacePushSubscription,
  subscriptionToPayload,
  urlBase64ToUint8Array,
  writeStoredVapidKey,
} from '@/components/notifications/webPushLogic.mjs'

const SW_URL = `${process.env.BASE_URL || '/'}sw.js`

let registrationPromise = null

export async function registerPushServiceWorker() {
  if (!isWebPushSupported()) return null
  if (!registrationPromise) {
    registrationPromise = (async () => {
      try {
        await navigator.serviceWorker.register(SW_URL, { scope: '/' })
        return await navigator.serviceWorker.ready
      } catch (err) {
        registrationPromise = null
        console.warn('web push: service worker register failed', err)
        return null
      }
    })()
  }
  return registrationPromise
}

async function currentPushSubscription() {
  const reg = await registerPushServiceWorker()
  if (!reg?.pushManager) return null
  return reg.pushManager.getSubscription()
}

/**
 * Подписывает браузер на Push API и сохраняет endpoint в message-service.
 * @param {{ requestPermission?: boolean }} opts
 * @returns {Promise<boolean>} true если подписка на сервере
 */
export async function syncWebPushSubscription({ requestPermission = false } = {}) {
  if (!isWebPushSupported()) return false
  if (isImpersonationTab()) return false
  if (!getAccessToken()) return false

  try {
    const vapid = parseVapidKeyPayload(await fetchVapidPublicKey())
    if (!vapid.enabled) return false

    if (typeof Notification === 'undefined') return false
    let permission = Notification.permission
    if (permission === 'default' && requestPermission) {
      permission = await Notification.requestPermission()
    }
    if (permission !== 'granted') return false

    const reg = await registerPushServiceWorker()
    if (!reg?.pushManager) return false

    let sub = await reg.pushManager.getSubscription()
    if (sub && shouldReplacePushSubscription(readStoredVapidKey(), vapid.publicKey)) {
      await sub.unsubscribe()
      sub = null
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
      })
    }
    const payload = subscriptionToPayload(sub)
    if (!payload) return false
    await savePushSubscription(payload)
    writeStoredVapidKey(vapid.publicKey)
    return true
  } catch (err) {
    console.warn('web push: sync failed', err)
    return false
  }
}

/** Снимает подписку браузера и удаляет её в message-service (logout). */
export async function unsyncWebPushSubscription() {
  if (!isWebPushSupported()) return
  try {
    const sub = await currentPushSubscription()
    if (!sub) return
    const payload = subscriptionToPayload(sub)
    if (payload && getAccessToken()) {
      try {
        await deletePushSubscription(payload)
      } catch {
        // logout всё равно продолжается
      }
    }
    await sub.unsubscribe()
    writeStoredVapidKey('')
  } catch (err) {
    console.warn('web push: unsync failed', err)
  }
}
